import React, { useEffect, useRef, useState } from "react";
import { DisciplineWithPartners } from "components/Partners/PartnerTradeLabel";
import { setSidebarStatus } from "actions/user";
import { ALL_PERMISSIONS } from "constants/permissionsConstants";
import { useHttp } from "hooks/useHttp";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import Arrow from "assets/images/leftArr.png";
import SortImg from "assets/images/sort.png";
import Loader from "components/Loader";
import {
  sortByDate,
  FROM_NEWER_TO_OLDER,
  FROM_OLDER_TO_NEWER,
} from "helpers/sortByDate";
import { CSVLink } from "react-csv";
import { getDocControl } from "api/docControl/getDocControl";
import useLastProject from "components/Coordination/useLastProject";
import Pagination from "components/Pagination/Pagination";
import Select from "react-select";
import moment from "moment";
import addNotification from "helpers/notify";
import { url, get, tokenRef } from "api";
import ShareResourceModal from "components/Share/ShareResourceModal";
import ShareManySheetsModal from "components/Share/ShareManySheetsModal";
import UploadDocumentModal from "components/DocumentControl/UploadDocumentModal";
import { KIND_LABEL, canPreview, docKindOf, extOf } from "helpers/docTypes";
import usePermissions from "hooks/usePermissions";
import Preview from "./Preview";
import { getDiscipline } from "api/discipline/getDiscipline";
import { getDocByID } from "api/docControl/getDocByID";
import JSZip from "jszip";
import { resolveDocumentUrl, renderPdfThumbnail } from "helpers/pdfPreview";
import { eventTrack } from "helpers/ga4Helper";
import { Modal, Table, Tag, Button, Select as AntSelect, message } from "antd";
import SheetCanvasDrawer from "components/DocumentControl/SheetCanvasDrawer";
import SortableTh, { useTableSort } from "components/Table/SortableTh";
import IssueTool from "../../components/DocumentControl/IssueTool/IssueTool"; // Import the new component
import { usePreference } from "helpers/userPrefs";
import VersionChip from "components/Documents/VersionChip";
import ShopDrawingFilterBar, { useShopDrawingFilters } from "pages/ShopDrawings/ShopDrawingFilterBar";
import RemoteShopDrawingsButton from "components/Integrations/RemoteShopDrawingsButton";
import { getShopDrawingFacets, shopDrawingFilterQuery } from "api/tracking/shopDrawingFilterApi";
import PrivateDocuments from "./PrivateDocuments";

// Columns where "sort by this column" means biggest or newest first - a
// date, a count, a revision number. Everything else reads better A-Z.
const NUMERIC_COLUMNS = ["versionCount", "revisionDate", "revisionNumber", "sequenceNumber", "updatedAt", "createdAt"];

// THE CSV, COLUMN BY COLUMN AS THE TABLE SHOWS THEM (owner, 2026-10-23: "do
// the same discipline fix on the document control export"). It used to be
// the server's rows as they came, so Discipline and Project Name printed
// "[object Object]" beside every id and link. Discipline is the server's
// exportDiscipline (utils/docControlExportDiscipline): the document's own,
// or - for one in the Issues / Shop Drawings bucket - its issue's or shop
// drawing's. Under a contractor filter, Contractor leads.
const CSV_TYPE_LABEL = {
  "issue-media": "Issue media", "levels-areas": "Levels & Areas", "partner-documentation": "Partner documentation",
  "rfi-example": "RFI example", generated: "Generated", "shop-drawing": "Shop drawing",
};
const csvDay = (v) => (v ? moment(v).format("DD/MM/YYYY") : "");
export const csvRowsOf = (docs) => {
  const list = docs || [];
  const withContractor = list.some((d) => d && "contractorName" in d);
  return list.map((d) => ({
    ...(withContractor ? { Contractor: d.contractorName || "" } : {}),
    V: d.versionCount ?? "",
    Type: CSV_TYPE_LABEL[d.docType] || "Sheet",
    Discipline: d.exportDiscipline || d.discipline?.key || d.discipline?.value || "",
    "Project Name": d.projectId?.title || "",
    "Revision Date": csvDay(d.revisionDate),
    "Revision Number": d.revisionNumber ?? "",
    "Revit File Name": d.revitFileName || "",
    "Sequence Number": d.sequenceNumber ?? "",
    "Sheet Name": d.sheetName || "",
    "Sheet Number": d.sheetNumber || "",
    "Sheet Status": d.sheetStatus || "",
    "Upload Date": csvDay(d.updatedAt),
  }));
};

export default function DocControl() {
  const dispatch = useDispatch();
  const history = useHistory();
  const userInfoRedux = useSelector((s) => s.user.userInfo);
  const usersPermissionRedux = useSelector((s) => s.user.userPermission);
  const companyPermissionRedux = useSelector((s) => s.user.companyPermission);
  const allPermissionRedux = useSelector((s) => s.user.allPermission);
  const FinalPermissions = allPermissionRedux
    ?.filter((element) => companyPermissionRedux?.includes(element))
    ?.filter((i) => usersPermissionRedux?.includes(i));

  const [coversheet, setCoversheet] = useState();
  const [totalRecords, setTotalRecords] = useState();
  const [totalPage, setTotalPage] = useState();
  const [page, setPage] = useState(0);
  const [columnNameOptions, setColumnNameOptions] = useState();
  const [columnNameSelectValue, setColumnNameSelectValue] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [csvFileData, setCSVFileData] = useState([]);
  const [sortedToNewer, setSortedToNewer] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);
  const [enableDownloadPackage, setEnableDownloadPackage] = useState(false);
  const [disciplines, setDisciplines] = useState([]);
  // Which project's documents are on screen. The page used to list every
  // project at once, so a company's whole drawing set arrived in one
  // table; it now opens on ONE project - the same one the coordination
  // pages remember, so moving between them stays on the same job - with
  // "All my projects" still available for a cross-project look.
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useLastProject(projects);
  const [allProjects, setAllProjects] = useState(false);
  // Nothing is fetched until the picker has resolved: a first pass with no
  // project would flash every project's documents on screen, which is the
  // thing this page is meant to stop doing.
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  // Filed today, whichever project.
  const [uploadedToday, setUploadedToday] = useState(false);
  // "Hide issue media" - on by default, so the register opens on the
  // drawing set and not on every screenshot ever pasted onto an issue.
  // Remembered per person on the server (helpers/userPrefs); a view
  // preference, so Clear leaves it alone.
  const hideMediaKey = `doc_control_hide_issue_media_${userInfoRedux?.id || userInfoRedux?._id || "anon"}`;
  const [hideMediaPref, setHideMediaPref] = usePreference("docControl.hideIssueMedia", true, { legacyKey: hideMediaKey });
  const hideIssueMedia = !(hideMediaPref === false || hideMediaPref === 0 || hideMediaPref === "0");
  const setHideIssueMedia = (v) => { setHideMediaPref(!!v); setPage(0); };

  // Issue tool state
  const [issueOpen, setIssueOpen] = useState(false);
  const [selectedDocForIssue, setSelectedDocForIssue] = useState(null);
  // The version history of one sheet, loaded on demand: the table only
  // ever receives a COUNT, so the list is fetched when somebody asks.
  const [versionsFor, setVersionsFor] = useState(null);
  const [versionsBusy, setVersionsBusy] = useState(false);
  // The sheet open on the canvas, with its related-sheets panel.
  const [canvasSheet, setCanvasSheet] = useState(null);
  const [documentThumbnails, setDocumentThumbnails] = useState({});

  // ?sheet=<id> - the sheet a share link names.
  //
  // A partner who is sent a sheet now gets a link into the real page
  // rather than the read-only shell, and a link to a page is no use if it
  // opens the whole register and leaves them to find the drawing. Fetched
  // by id rather than searched for in the current table page: the sheet
  // they were sent is frequently not on page 1 of their own project's
  // register, and it must open whatever the table is showing. The param
  // is dropped afterwards so a refresh doesn't reopen it.
  useEffect(() => {
    let sheetParam = "";
    try { sheetParam = new URLSearchParams(window.location.search).get("sheet") || ""; } catch { sheetParam = ""; }
    if (!sheetParam) return;
    let alive = true;
    (async () => {
      try {
        const res = await getDocByID(sheetParam);
        const doc = res?.document || res?.data || res;
        if (alive && doc && doc._id) setCanvasSheet(doc);
      } catch (e) { /* a revoked or deleted sheet just leaves the register open */ }
      try {
        const u = new URL(window.location.href);
        u.searchParams.delete("sheet");
        window.history.replaceState({}, "", u.pathname + (u.search || "") + (u.hash || ""));
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, []);

  const data = useRef({ index: 0, limit: 10 });

  // The table's columns, in order.
  const coversheetHeaders = [
    // How many versions of THIS sheet the register holds. It moves every
    // time the same sheet number arrives with different bytes - a
    // reissued set, or a file replaced by hand.
    { header: "V", value: "versionCount" },
    // A drawing, or media mirrored from an issue (pasted screenshots,
    // attachments, resolution evidence). Mirrors sit under the ISSUE's
    // discipline, so without this column an electrical clash's screenshot
    // is indistinguishable from an electrical sheet.
    { header: "Type", value: "docType" },
    { header: "Discipline", value: "discipline" },
    { header: "Project Name", value: "projectId" },
    { header: "Revision Date", value: "revisionDate" },
    { header: "Revision Number", value: "revisionNumber" },
    { header: "Revit File Name", value: "revitFileName" },
    { header: "Sequence Number", value: "sequenceNumber" },
    { header: "Sheet Name", value: "sheetName" },
    { header: "Sheet Number", value: "sheetNumber" },
    { header: "Sheet Status", value: "sheetStatus" },
    { header: "Upload Date", value: "updatedAt" },
  ];

  // What the search box can be pointed at. Everything on the row, plus
  // the properties that are stored but not shown as a column. Leaving
  // the category empty searches ALL of them at once. Dates take YYYY,
  // YYYY-MM or YYYY-MM-DD.
  const searchableColumns = [
    ...coversheetHeaders,
    { header: "Description", value: "description" },
    { header: "Package / project number", value: "package" },
    { header: "Floor level", value: "floorLevel" },
    { header: "Views", value: "views" },
    { header: "Uploaded by", value: "exportedBy" },
    { header: "Uploaded date", value: "createdAt" },
  ];
  const DATE_COLUMNS = ["revisionDate", "updatedAt", "createdAt"];

  const { loading: apiLoading, request: getDocAPI } = useHttp({
    requestCallback: (data) => getDocControl(data?.current),
    onLoad: (res) => {
      console.log(res);
      setCoversheet(res?.documents);
      setTotalRecords(res?.totalCount);
      setTotalPage(Math.ceil(res?.totalCount / data?.current?.limit));
    },
    onError: (res) => {
      console.log("err", res);
      addNotification("Something went wrong", "danger");
    },
  });

  const { loading: downloadLoading, request: DownloadCoversheet } = useHttp({
    requestCallback: (data) => getDocControl(data),
    onLoad: (res) => {
      // Under a contractor filter each row carries the picked contractor it is
      // under: first column, headed as the issue and drawing exports head it.
      setCSVFileData(csvRowsOf(res?.documents));
    },
    onError: (res) => {
      console.log("err", res);
    },
  });

  const { loading: disciplineLoading, request: getDisciplineAPI } = useHttp({
    requestCallback: (data) => getDiscipline(userInfoRedux.company),
    onLoad: (res) => {
      setDisciplines(res);
    },
    onError: (res) => {
      addNotification("Something went wrong, please contact support", "danger");
    },
  });

  // Order the register by any column the server can order by.
  //
  // Declared HERE, above the effect that sorts by it, and not down with
  // the rest of the state. A dependency array is evaluated during
  // RENDER, so `[..., sorter.field, sorter.dir]` on the effect below
  // reads this const at that point in the body. While it was declared
  // further down, that read hit the temporal dead zone and threw
  // "Cannot access 'sorter' before initialization" on every render -
  // the whole page came up blank, with the error pointing at the
  // dependency array rather than at the declaration that was too late.
  const sorter = useTableSort("updatedAt", -1, "docControl.sort");

  // EVERY SHOP DRAWING PROPERTY AS A FILTER (owner, 2026-10-23: "add same
  // filter from issues manager, shop drawings, and contractor cost" - "all the
  // shopdrawings columns/properties"). The register's own bar and filter
  // object (pages/ShopDrawings/ShopDrawingFilterBar), answered by the
  // register's own query on the server: the documents left are the ones the
  // matching drawings filed. One project at a time - a drawing belongs to one.
  const sdProject = allProjects ? "" : (projectId || "");
  const [sdFacets, setSdFacets] = useState(null);
  const [sdOpen, setSdOpen] = useState(false);
  // The bar's area sign-off counts are read only once it is opened.
  const sf = useShopDrawingFilters(sdOpen && sdFacets ? sdProject : "");
  useEffect(() => {
    let alive = true;
    setSdFacets(null);
    if (!sdProject) return undefined;
    // No facets = the register is not this person's to read; the bar stays away.
    getShopDrawingFacets(sdProject).then((f) => { if (alive) setSdFacets(f); }).catch(() => {});
    return () => { alive = false; };
  }, [sdProject]);
  // A filter changes the rows a project has; a new project starts clean.
  useEffect(() => { sf.setFilters({}); }, [sdProject]); // eslint-disable-line react-hooks/exhaustive-deps

  // THE CONTRACTOR (owner, 2026-10-23: "do the same contractor filter on the
  // document control export"): the documents the picked contractors' shop
  // drawings filed, in their trades, or uploaded by their people - answered
  // by the server (utils/docControlContractorScope), so the list, the CSV and
  // the download package hold the same documents, and the CSV's Contractor
  // column names a picked contractor only. The team's, on one project: the
  // choices come with the shop drawing facets (none for a partner).
  const [dcContractors, setDcContractors] = useState([]);
  useEffect(() => { setDcContractors([]); }, [sdProject]);
  const contractorChoices = (sdFacets && sdFacets.contractors) || [];
  // Typing waits a moment, like the register's own search.
  const sdStampNow = sdProject && sdFacets && sf.anyFilter
    ? shopDrawingFilterQuery({ ...sf.filters, tzOffset: new Date().getTimezoneOffset() }) : "";
  const [sdStamp, setSdStamp] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setSdStamp(sdStampNow), sf.filters.search ? 350 : 0);
    return () => clearTimeout(t);
  }, [sdStampNow]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(0); }, [sdStamp]);

  // Filters that live on the server: the project, the "uploaded today"
  // toggle, and whatever the search box asks for.
  const serverFilters = () => ({
    projectId: allProjects ? undefined : projectId || undefined,
    uploadedToday: uploadedToday ? true : undefined,
    hideIssueMedia: hideIssueMedia ? true : undefined,
    shopDrawing: sdStamp ? encodeURIComponent(sdStamp) : undefined,
    contractor: sdProject && dcContractors.length ? dcContractors.join(",") : undefined,
  });

  useEffect(() => {
    if (!projectsLoaded) return;
    data.current = {
      ...data.current,
      index: page,
      column: columnNameSelectValue?.value,
      search: searchValue,
      ...serverFilters(),
      sort: sorter.field,
      sort_dir: sorter.dir,
    };
    getDocAPI(data);
    // The CSV / download package follows the same filters - exporting the
    // whole company from a filtered table is not what the button says.
    DownloadCoversheet({ sort: sorter.field, sort_dir: sorter.dir, ...serverFilters(), forExport: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, projectId, allProjects, uploadedToday, hideIssueMedia, projectsLoaded, sorter.field, sorter.dir, sdStamp, dcContractors.join(",")]);

  useEffect(() => {
    setColumnNameOptions(
      searchableColumns.map((value) => ({ value: value.value, label: value.header }))
    );
    getDisciplineAPI();
    // Projects the caller may see (the picker endpoint: {_id,title} only).
    get("/projects/picker")
      .then((r) => {
        const list = Array.isArray(r) ? r : r?.projects || [];
        setProjects(list);
        setProjectId((cur) => (cur && list.some((p) => p._id === cur) ? cur : list[0]?._id || ""));
      })
      .catch(() => setProjects([]))
      .finally(() => setProjectsLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Read-only sharing of ONE sheet. Held as the sheet itself rather than
  // a boolean so the modal knows which row it was opened from.
  const [shareSheet, setShareSheet] = useState(null);
  // Sheets ticked for a bulk share. Held as ids rather than rows so a
  // refresh cannot leave the selection holding stale copies of records
  // that have since been revised.
  const [selectedIds, setSelectedIds] = useState([]);
  const [shareManyOpen, setShareManyOpen] = useState(false);
  // Upload a document straight from the browser. Until now Document
  // Control could only RECORD a link the Revit plugin had already put
  // somewhere - there was no way to file a PDF from here at all.
  const [uploadOpen, setUploadOpen] = useState(false);
  // The API allows sharing only from the project's owner side and answers
  // 403 otherwise; docControlEdit is the closest client-side signal, so
  // the button follows it rather than appearing for people who would only
  // get an error.
  const { has: hasPerm, isAdmin } = usePermissions();
  // isAdmin as well as the permission: a company/super admin's cached
  // permission set does not always enumerate every key, and hiding the
  // button from an admin reads as "the feature is missing" rather than
  // "you lack a permission".
  const canShareSheets = isAdmin || hasPerm(ALL_PERMISSIONS.DOC_CONTROL_EDIT);

  const visibleRows = (coversheet || []).slice(0, data.current?.limit);
  const selectedSet = new Set(selectedIds.map(String));
  const selectedRows = visibleRows.filter((r) => selectedSet.has(String(r._id)));
  const allVisibleSelected = visibleRows.length > 0 && selectedRows.length === visibleRows.length;
  const toggleOne = (id) => setSelectedIds((cur) => (
    cur.map(String).includes(String(id))
      ? cur.filter((x) => String(x) !== String(id))
      : [...cur, String(id)]
  ));
  useEffect(() => {
    setSelectedIds((cur) => {
      const onScreen = new Set(visibleRows.map((r) => String(r._id)));
      const kept = cur.filter((id) => onScreen.has(String(id)));
      return kept.length === cur.length ? cur : kept;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coversheet]);

  const toggleAllVisible = () => setSelectedIds(
    allVisibleSelected ? [] : visibleRows.map((r) => String(r._id))
  );

  const onDropdownChange = (e) => {
    if (e.value == columnNameSelectValue.value)
      setColumnNameSelectValue({ label: "", value: "" });
    else setColumnNameSelectValue(e);
  };

  const onSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  // Re-run whatever is on screen (the Refresh button).
  const refresh = () => {
    data.current = { ...data.current, index: page, ...serverFilters() };
    getDocAPI(data);
  };

  const onClearFunc = () => {
    setSearchValue("");
    setColumnNameSelectValue({ label: "", value: "" });
    setUploadedToday(false);
    data.current = { ...data.current, index: 0, search: null, column: null, uploadedToday: undefined };
    getDocAPI(data);
    setEnableDownloadPackage(false);
  };

  const SearchFunction = () => {
    setCoversheet([]);
    data.current = {
      ...data.current,
      index: 0,
      column: columnNameSelectValue.value,
      search: searchValue,
      ...serverFilters(),
    };
    getDocAPI(data);
    setTotalPage(0);
    setPage(0);
    setEnableDownloadPackage(true);
  };

  const sort = () => {
    if (sortedToNewer) {
      setCoversheet(sortByDate(coversheet, FROM_OLDER_TO_NEWER));
    } else {
      setCoversheet(sortByDate(coversheet, FROM_NEWER_TO_OLDER));
    }
  };

  // Download a document: gzipped sheets are inflated client-side; files
  // mirrored from issues (png / jpg / pdf / xlsx ...) download as they are.
  const extractPdfFromZip = async (uri) => {
    try {
      const src = await resolveDocumentUrl(uri);
      const link = document.createElement("a");
      link.href = src;
      link.download = String(uri || "document").split("/").pop().replace(/\.gz$/i, "");
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return src;
    } catch (error) {
      console.error("Error downloading document:", error);
      throw error;
    }
  };

  // DOWNLOAD THE TICKED DOCUMENTS (owner 2026-11-03, reported by Pavlo at BES: download and
  // view had "stopped working", and no download button appeared when a checkbox was ticked).
  //
  // There was no such button. The only selection-driven action was "Share selected", gated on
  // canShareSheets = isAdmin || DOC_CONTROL_EDIT - a permission a PARTNER does not have - so
  // ticking a box showed a partner nothing at all. The separate "Download package" button is
  // not selection-driven either: it appears only after a Search (setEnableDownloadPackage is
  // set in SearchFunction and nowhere else), which is why it looked like downloading had
  // stopped working rather than never having been offered.
  //
  // Downloading what you can already see is a READ. It is gated on nothing beyond reaching
  // this page, which the view permission already decides - exactly as the per-row Download
  // button at the end of each row has always been.
  //
  // Sequential, not Promise.all: each file is a real anchor click, and browsers drop
  // simultaneous downloads. A failure on one is reported and the rest still run.
  const [downloadingSelected, setDownloadingSelected] = useState(false);
  const downloadSelected = async () => {
    const rows = selectedRows.filter((r) => r && r.link);
    if (!rows.length) return;
    setDownloadingSelected(true);
    let failed = 0;
    for (const r of rows) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await extractPdfFromZip(r.link);
        // eslint-disable-next-line no-await-in-loop
        await new Promise((ok) => setTimeout(ok, 350));
      } catch (e) { failed += 1; }
    }
    setDownloadingSelected(false);
    // antd's message, not toast: toast is not imported in this file and adding it for one line
    // would be a new dependency on a page that reports everything else through console.error.
    // A failed download the user asked for should still be VISIBLE, though - silence here reads
    // as "it stopped working", which is how this was reported in the first place.
    if (failed) message.error(`${failed} of ${rows.length} document(s) could not be downloaded.`);
  };

  // First-page thumbnail (or the image itself) via the shared helper: works
  // for gz PDFs on R2 and for images / PDFs mirrored from issues.
  const generateThumbnail = async (link, docId) => {
    // A DWG, a model or a spreadsheet has no first page to draw.
    if (!canPreview(link)) return;
    try {
      const thumbnailUrl = await renderPdfThumbnail(link);
      if (thumbnailUrl) setDocumentThumbnails((prev) => ({ ...prev, [docId]: thumbnailUrl }));
    } catch (error) {
      console.error('Error generating thumbnail:', error);
    }
  };

  // Generate thumbnails for visible documents
  useEffect(() => {
    if (coversheet && coversheet.length > 0) {
      coversheet.slice(0, data.current?.limit).forEach((item, index) => {
        if (item._id && !documentThumbnails[item._id]) {
          generateThumbnail(item.link, item._id);
        }
      });
    }
  }, [coversheet]);

  // Fetch one sheet's history. The list endpoint only ever sends a
  // count, so this is the first time the versions themselves are read.
  const openVersions = async (item) => {
    setVersionsFor({ doc: item, versions: [] });
    setVersionsBusy(true);
    try {
      const res = await fetch(`${url}/doc-control/${item._id}/versions`, {
        headers: { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` },
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.err || `HTTP ${res.status}`);
      setVersionsFor({ doc: item, ...body });
    } catch (e) {
      addNotification({ title: "Versions", message: e.message || "Could not read the versions", type: "danger" });
      setVersionsFor(null);
    } finally {
      setVersionsBusy(false);
    }
  };

  const openIssueTool = async (item) => {
    try {
      // (blob URLs are cached per link by helpers/pdfPreview - not revoked here)

      console.log('Opening issue for:', item?.sheetName, 'Link:', item?.link);
      
      // gz sheets -> inflated blob URL; images / plain PDFs -> absolute URL
      const pdfUrl = await resolveDocumentUrl(item?.link);
      
      // Set document data with unique timestamp to force re-render
      const docData = {
        ...item,
        url: pdfUrl,
        name: item?.sheetName || item?.revitFileName,
        timestamp: Date.now() // Add timestamp to make each document unique
      };
      
      setSelectedDocForIssue(docData);
      setIssueOpen(true);
      
      eventTrack(
        "Open-Issue-Tool-Action",
        "Open-Issue-Tool",
        userInfoRedux?.name,
        "+1",
        false,
        "+1"
      );
    } catch (error) {
      console.error("Error loading document for issue:", error);
      addNotification("Failed to load document for issue", "danger");
    }
  };

  const handleSaveIssue = async (issueInfo) => {
    try {
      // Here you would save to your backend
      // For now, we'll generate a shareable URL
      const issueId = `issue_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const shareableUrl = `${window.location.origin}/issue/${issueId}`;
      
      // Store issue data (you would send this to your backend)
      console.log('Saving issue:', {
        id: issueId,
        ...issueInfo,
        documentId: selectedDocForIssue?._id
      });
      
      // Show success notification
      addNotification("Issue saved successfully!", "success");
      
      return shareableUrl;
    } catch (error) {
      console.error('Error saving issue:', error);
      throw error;
    }
  };

  return (
    <>
      {apiLoading && <Loader />}
      <div className="personal-area">
        <div className="inner-header-position">
          <div className="custom-container d-flex align-items-center justify-content-between flex-wrap">
            <div className="d-flex align-items-center">
              <div
                onClick={() => dispatch(setSidebarStatus(true))}
                className="cursor-pointer"
              >
                <img src={Arrow} className="left-arrow-img" />
              </div>
              <p className="my-heading f-14 ml-3 mr-3">Doc Control</p>
            </div>
            <div
              className="projActionButtons d-flex flex-wrap"
              style={{ maxWidth: "100%" }}
            >
              {/* One project at a time, remembered across the coordination
                  suite. "All my projects" is still there for a look across
                  the ones this account is on - never the whole company for
                  somebody who is not an admin (the server scopes it). */}
              <Select
                onChange={(e) => {
                  setPage(0);
                  if (e?.value === "__all__") { setAllProjects(true); }
                  else { setAllProjects(false); setProjectId(e?.value || ""); }
                }}
                options={[
                  { value: "__all__", label: "All my projects" },
                  ...projects.map((p) => ({ value: p._id, label: p.title || "Untitled" })),
                ]}
                placeholder="Project"
                value={
                  allProjects
                    ? { value: "__all__", label: "All my projects" }
                    : projects
                        .filter((p) => p._id === projectId)
                        .map((p) => ({ value: p._id, label: p.title || "Untitled" }))[0] || null
                }
                className="header-select mr-2"
                classNamePrefix="header-select"
              />
              <Select
                onChange={(e) => onDropdownChange(e)}
                options={columnNameOptions}
                placeholder="All columns"
                value={columnNameSelectValue}
                selected={columnNameSelectValue}
                className="header-select"
                classNamePrefix="header-select"
              />
              <div className="input-with-cancelBtn">
                {/* No category picked searches every column at once. */}
                <input
                  type="text"
                  className="search-bar"
                  value={searchValue}
                  onChange={(e) => onSearchChange(e)}
                  onKeyDown={(e) => { if (e.key === "Enter" && searchValue) SearchFunction(); }}
                  placeholder={
                    !columnNameSelectValue?.value
                      ? "Search all columns"
                      : DATE_COLUMNS.includes(columnNameSelectValue.value)
                      ? "YYYY, YYYY-MM or YYYY-MM-DD"
                      : `Search ${columnNameSelectValue.label}`
                  }
                />
                {searchValue?.length > 0 && (
                  <button onClick={() => onClearFunc()}>
                    <i className="fa fa-times" />
                  </button>
                )}
              </div>

              <button
                disabled={!searchValue?.length > 0}
                onClick={() => SearchFunction()}
                className="global-btn bg-bid text-white mr-0"
              >
                <i className="fa fa-search mr-2" />
                Search
              </button>
              {/* The table search above matches document METADATA. This
                  one goes inside the sheets - and into the model beside
                  them - for the project currently picked. Disabled under
                  "All my projects" because a search needs one project's
                  index (docs/2d-3d-search.md). */}
              <button
                disabled={allProjects || !projectId}
                title={
                  allProjects || !projectId
                    ? "Pick a single project to search its sheets and model"
                    : "One query across this project's sheets and its model"
                }
                onClick={() => history.push(`/split-search?projectId=${projectId}`)}
                className="global-btn bg-white text-bid shadow-hover ml-2 mr-0"
              >
                <i className="fa fa-vector-square mr-2" />
                Find in Sheets &amp; Model
              </button>
              <button
                onClick={() => { setPage(0); setUploadedToday((v) => !v); }}
                title="Documents filed today, whichever project"
                className={`global-btn ml-2 mr-0 ${uploadedToday ? "bg-bid text-white" : "bg-white text-bid shadow-hover"}`}
              >
                <i className="fa fa-calendar-day mr-2" />
                Uploaded today
              </button>
              <label
                className="ml-3 mb-0 d-flex align-items-center"
                style={{ fontSize: 12.5, whiteSpace: "nowrap", cursor: "pointer" }}
                title="Only drawings - none of the screenshots and attachments mirrored off issues. On by default"
              >
                <input type="checkbox" checked={hideIssueMedia} onChange={(e) => setHideIssueMedia(e.target.checked)} style={{ marginRight: 6 }} />
                Hide issue media
              </label>
              {sdProject && contractorChoices.length ? (
                <AntSelect
                  allowClear mode="multiple" maxTagCount="responsive" placeholder="Contractor"
                  aria-label="Document contractor filter" optionFilterProp="label" size="small"
                  className="ml-3" style={{ minWidth: 180 }}
                  value={dcContractors}
                  onChange={(v) => { setDcContractors(v || []); setPage(0); }}
                  options={contractorChoices.map((c) => ({ value: String(c.companyId), label: c.companyName || String(c.companyId) }))}
                />
              ) : null}
              {enableDownloadPackage && (
                <button
                  className="global-btn bg-bid text-white ml-2 mr-0"
                  onClick={() => setOpenPreview(true)}
                >
                  <i className="fa fa-download mr-2" />
                  Download package
                </button>
              )}
              <div className="vr mx-3"></div>
              <div className="btn-tool-group">
                {(userInfoRedux?.admin || userInfoRedux?.companyAdmin) && (
                  <>
                    <button
                      onClick={() => {
                        if (
                          FinalPermissions?.includes(
                            ALL_PERMISSIONS.PROJECT_VIEW
                          )
                        ) {
                          history.push(`/admin/projects-settings`);
                        } else if (
                          FinalPermissions?.includes(
                            ALL_PERMISSIONS.MILESTONE_VIEW
                          )
                        ) {
                          history.push(`/admin/milestones`);
                        } else {
                          history.push(`/admin/user-permissions`);
                        }
                        eventTrack(
                          "Navigate-Admin-Section-Action",
                          "Navigate-Admin-Section",
                          userInfoRedux?.name,
                          "+1",
                          false,
                          "+1"
                        );
                      }}
                      className="bg-bid px-3 mr-0"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="19"
                        viewBox="0 0 14 19"
                        fill="none"
                      >
                        <path
                          d="M0.333984 18.3335C0.333984 14.6516 3.31875 11.6668 7.00065 11.6668C10.6826 11.6668 13.6673 14.6516 13.6673 18.3335H0.333984ZM7.00065 10.8335C4.23815 10.8335 2.00065 8.596 2.00065 5.8335C2.00065 3.071 4.23815 0.833496 7.00065 0.833496C9.76315 0.833496 12.0007 3.071 12.0007 5.8335C12.0007 8.596 9.76315 10.8335 7.00065 10.8335Z"
                          fill="white"
                        />
                      </svg>
                      Admin
                    </button>
                    <div className="vr mx-3"></div>
                  </>
                )}

                <button
                  onClick={() => {
                    setSortedToNewer(!sortedToNewer);
                    sort();
                    eventTrack(
                      "Sort-Doc-Control-Action",
                      "Sort-Doc-Control",
                      userInfoRedux?.name,
                      "+1",
                      false,
                      "+1"
                    );
                  }}
                  className="px-3 bg-white text-bid shadow-hover d-flex align-items-center leading-1"
                >
                  <img
                    src={SortImg}
                    className={`${"w-15 mr-2"} ${
                      sortedToNewer ? "upsideDown-svg" : ""
                    } `}
                  />
                  Sort
                </button>

                {selectedRows.length > 0 && (
                  <button
                    className="global-btn bg-bid text-white ml-2 mr-0"
                    onClick={downloadSelected}
                    disabled={downloadingSelected}
                    title="Download the ticked documents"
                  >
                    <i className="fa fa-download mr-2" />
                    {downloadingSelected ? "Downloading..." : `Download selected (${selectedRows.length})`}
                  </button>
                )}
                {canShareSheets && selectedRows.length > 0 && (
                  <button
                    onClick={() => setShareManyOpen(true)}
                    className="px-3 bg-white text-bid shadow-hover d-flex align-items-center leading-1"
                    title="Share every ticked sheet with the same people"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                         fill="currentColor" viewBox="0 0 16 16" className="mr-2">
                      <path d="M13.5 1a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3M11 2.5a2.5 2.5 0 1 1 .603 1.628l-6.718 3.12a2.5 2.5 0 0 1 0 1.504l6.718 3.12a2.5 2.5 0 1 1-.488.876l-6.718-3.12a2.5 2.5 0 1 1 0-3.256l6.718-3.12A2.5 2.5 0 0 1 11 2.5m-8.5 4a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3m11 5.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3" />
                    </svg>
                    Share selected ({selectedRows.length})
                  </button>
                )}

                {canShareSheets && (
                  <button
                    onClick={() => setUploadOpen(true)}
                    className="px-3 bg-white text-bid shadow-hover d-flex align-items-center leading-1"
                    title="Upload documents of any kind the project needs (PDF, DWG, models, Excel, schedules, Word, ZIP) and file them in Document Control"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                         fill="currentColor" viewBox="0 0 16 16" className="mr-2">
                      <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
                      <path d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708z" />
                    </svg>
                    Upload
                  </button>
                )}

                {/* SHOP DRAWINGS FROM PROCORE / ACC (owner 2026-10-25): each account's shop
                    drawings DOWNLOAD folder, filed here as shop-drawing documents */}
                {canShareSheets && (
                  <RemoteShopDrawingsButton
                    projectId={allProjects ? "" : projectId}
                    projectTitle={((projects || []).find((p) => p._id === projectId) || {}).title}
                    className="bg-white text-bid shadow-hover"
                    size="middle"
                    onFiled={() => refresh()}
                  />
                )}

                <button
                  disabled={apiLoading}
                  onClick={() => {
                    refresh();
                  }}
                  className="px-3 bg-white text-bid shadow-hover"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="17"
                    viewBox="0 0 18 17"
                    fill="none"
                  >
                    <path
                      d="M3.55149 2.19418C5.01232 0.931059 6.9166 0.166992 8.99935 0.166992C13.6017 0.166992 17.3327 3.89795 17.3327 8.50033C17.3327 10.2804 16.7745 11.9302 15.8237 13.2842L13.166 8.50033H15.666C15.666 4.81843 12.6813 1.83366 8.99935 1.83366C7.20753 1.83366 5.58082 2.54055 4.38287 3.69068L3.55149 2.19418ZM14.4472 14.8065C12.9863 16.0696 11.0821 16.8337 8.99935 16.8337C4.39697 16.8337 0.666016 13.1027 0.666016 8.50033C0.666016 6.72021 1.22417 5.07046 2.17502 3.71653L4.83268 8.50033H2.33268C2.33268 12.1822 5.31745 15.167 8.99935 15.167C10.7912 15.167 12.4179 14.4601 13.6159 13.31L14.4472 14.8065Z"
                      fill="#3A5765"
                    />
                    <path
                      d="M3.55149 2.19418C5.01232 0.931059 6.9166 0.166992 8.99935 0.166992C13.6017 0.166992 17.3327 3.89795 17.3327 8.50033C17.3327 10.2804 16.7745 11.9302 15.8237 13.2842L13.166 8.50033H15.666C15.666 4.81843 12.6813 1.83366 8.99935 1.83366C7.20753 1.83366 5.58082 2.54055 4.38287 3.69068L3.55149 2.19418ZM14.4472 14.8065C12.9863 16.0696 11.0821 16.8337 8.99935 16.8337C4.39697 16.8337 0.666016 13.1027 0.666016 8.50033C0.666016 6.72021 1.22417 5.07046 2.17502 3.71653L4.83268 8.50033H2.33268C2.33268 12.1822 5.31745 15.167 8.99935 15.167C10.7912 15.167 12.4179 14.4601 13.6159 13.31L14.4472 14.8065Z"
                      fill="black"
                      fillOpacity="0.2"
                    />
                  </svg>
                  Refresh
                </button>
              </div>
              <div className="vr mx-2"></div>

              <div className="head-filter-icon" title="Download CSV">
                <CSVLink data={csvFileData} filename={"Doc"}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="#3a5765"
                    className="bi bi-filetype-csv"
                    viewBox="0 0 16 16"
                  >
                    <path
                      fillRule="evenodd"
                      d="M14 4.5V14a2 2 0 0 1-2 2h-1v-1h1a1 1 0 0 0 1-1V4.5h-2A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v9H2V2a2 2 0 0 1 2-2h5.5zM3.517 14.841a1.13 1.13 0 0 0 .401.823q.195.162.478.252.284.091.665.091.507 0 .859-.158.354-.158.539-.44.187-.284.187-.656 0-.336-.134-.56a1 1 0 0 0-.375-.357 2 2 0 0 0-.566-.21l-.621-.144a1 1 0 0 1-.404-.176.37.37 0 0 1-.144-.299q0-.234.185-.384.188-.152.512-.152.214 0 .37.068a.6.6 0 0 1 .246.181.56.56 0 0 1 .12.258h.75a1.1 1.1 0 0 0-.2-.566 1.2 1.2 0 0 0-.5-.41 1.8 1.8 0 0 0-.78-.152q-.439 0-.776.15-.337.149-.527.421-.19.273-.19.639 0 .302.122.524.124.223.352.367.228.143.539.213l.618.144q.31.073.463.193a.39.39 0 0 1 .152.326.5.5 0 0 1-.085.29.56.56 0 0 1-.255.193q-.167.07-.413.07-.175 0-.32-.04a.8.8 0 0 1-.248-.115.58.58 0 0 1-.255-.384zM.806 13.693q0-.373.102-.633a.87.87 0 0 1 .302-.399.8.8 0 0 1 .475-.137q.225 0 .398.097a.7.7 0 0 1 .272.26.85.85 0 0 1 .12.381h.765v-.072a1.33 1.33 0 0 0-.466-.964 1.4 1.4 0 0 0-.489-.272 1.8 1.8 0 0 0-.606-.097q-.534 0-.911.223-.375.222-.572.632-.195.41-.196.979v.498q0 .568.193.976.197.407.572.626.375.217.914.217.439 0 .785-.164t.55-.454a1.27 1.27 0 0 0 .226-.674v-.076h-.764a.8.8 0 0 1-.118.363.7.7 0 0 1-.272.25.9.9 0 0 1-.401.087.85.85 0 0 1-.478-.132.83.83 0 0 1-.299-.392 1.7 1.7 0 0 1-.102-.627zm8.239 2.238h-.953l-1.338-3.999h.917l.896 3.138h.038l.888-3.138h.879z"
                    />
                  </svg>
                </CSVLink>
              </div>
            </div>
          </div>
        </div>

        {/* Selection is over the rows ACTUALLY on screen: "select all"
            that quietly ticked rows below the paging limit would share
            documents nobody had looked at. */}
        {sdProject && sdFacets ? (
          <div style={{ marginTop: 10 }}>
            <Button size="small" type={sf.anyFilter ? "primary" : "default"} onClick={() => setSdOpen((v) => (sf.anyFilter ? true : !v))}
                    aria-expanded={sdOpen || sf.anyFilter}>
              {`Filter by shop drawing${sf.anyFilter ? ` (${Object.keys(sf.filters).length} on)` : ""}`}
            </Button>
            {sdOpen || sf.anyFilter ? (
              <div style={{ marginTop: 8 }}>
                <ShopDrawingFilterBar
                  projectId={sdProject} sf={sf} showDeliveredTo={!!sdFacets.ownerSide}
                  facets={{ areas: sdFacets.areas || [], packages: sdFacets.packages || [], levels: sdFacets.levels || [], contractors: sdFacets.contractors || [] }}
                  countText={sf.anyFilter ? "Only the documents the matching shop drawings filed" : "Any shop drawing property"}
                />
              </div>
            ) : null}
          </div>
        ) : null}
        <PrivateDocuments projectId={allProjects ? "" : (projectId || "")} />
        <div className="assestmgmt-table-div mt-3">
          <table className="bid-table bid-table-small border-top-none sticky-table-header">
            <thead>
              <tr>
                {canShareSheets && (
                  <th style={{ width: 34 }}>
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleAllVisible}
                      title={allVisibleSelected ? "Clear the selection" : "Select every sheet on screen"}
                    />
                  </th>
                )}
                <th>S.No</th>
                <th>Preview</th>
                {coversheetHeaders.map((value, key) => (
                  <SortableTh
                    key={key}
                    field={value.value}
                    numeric={NUMERIC_COLUMNS.includes(value.value)}
                    {...sorter.props}
                  >
                    {value.header}
                  </SortableTh>
                ))}
                <th>Actions</th>
              </tr>
            </thead>
            {!apiLoading && coversheet?.length !== 0 ? (
              <tbody className="no-wrap">
                {coversheet
                  ?.slice(0, data.current?.limit)
                  .map((item, index) => {
                    return (
                      <tr key={index}>
                        {canShareSheets && (
                          <td onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedSet.has(String(item._id))}
                              onChange={() => toggleOne(item._id)}
                            />
                          </td>
                        )}
                        <td>{index + 1}</td>
                        <td>
                          <div style={{ 
                            width: '80px', 
                            height: '100px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            backgroundColor: '#f3f4f6',
                            borderRadius: '4px',
                            overflow: 'hidden'
                          }}>
                            {documentThumbnails[item._id] ? (
                              <img 
                                src={documentThumbnails[item._id]} 
                                alt="Preview"
                                style={{ 
                                  maxWidth: '100%', 
                                  maxHeight: '100%',
                                  objectFit: 'contain'
                                }}
                              />
                            ) : !canPreview(item.link) ? (
                              <span title={KIND_LABEL[docKindOf(item.originalFileName || item.link)] || "File"}
                                    style={{ fontSize: '13px', fontWeight: 700, color: '#374151', border: '1px solid #9ca3af', borderRadius: 3, padding: '6px 4px', background: '#fff', letterSpacing: 0.5 }}>
                                {(extOf(item.originalFileName || item.link) || "file").toUpperCase()}
                              </span>
                            ) : (
                              <span style={{ fontSize: '10px', color: '#9ca3af' }}>Loading...</span>
                            )}
                          </div>
                        </td>
                        <td>
                          {/* Current revision, and the history behind it.
                              A sheet on its first issue is just "v0"; one
                              that has been reissued is worth clicking. */}
                          {(item?.versionCount || 0) > 1 ? (
                            <button
                              className="border-none bg-transparent p-0"
                              title={`${item.versionCount} versions - click to see them`}
                              onClick={() => openVersions(item)}
                              style={{ color: "#0d6efd", fontWeight: 600, cursor: "pointer" }}
                            >
                              {/* "v1" with a superscript 2 reads as "v12",
                                  which is exactly the misreading this
                                  column must not cause. The count is set
                                  apart instead. */}
                              v{item?.revisionNumber ?? 0}
                              <span style={{ marginLeft: 4, fontSize: 11, fontWeight: 400, color: "#6b7280" }}>
                                ({item.versionCount})
                              </span>
                            </button>
                          ) : (
                            <span style={{ color: "#9ca3af" }}>v{item?.revisionNumber ?? 0}</span>
                          )}
                        </td>
                        <td>
                          {item?.docType === "issue-media" ? (
                            <Tag color="gold" title={item?.sheetNumber ? `Mirrored from issue ${item.sheetNumber}` : "Mirrored from an issue"} style={{ margin: 0 }}>
                              Issue media
                            </Tag>
                          ) : item?.docType === "levels-areas" ? (
                            <Tag color="blue" title="Uploaded in Levels & Areas as a level's drawing" style={{ margin: 0 }}>
                              Levels &amp; Areas
                            </Tag>
                          ) : item?.docType === "partner-documentation" ? (
                            <Tag color="cyan" title="Imported on the Partners page to read partner companies and their people" style={{ margin: 0 }}>
                              Partner documentation
                            </Tag>
                          ) : item?.docType === "rfi-example" ? (
                            <Tag color="red" title="An RFI uploaded on the RFI template page to read the company's letterhead and wording" style={{ margin: 0 }}>
                              RFI example
                            </Tag>
                          ) : item?.docType === "generated" ? (
                            <Tag color="green" title="Produced in BidLight - an RFI package, a meeting pack, an estimate or a view" style={{ margin: 0 }}>
                              Generated
                            </Tag>
                          ) : item?.docType === "shop-drawing" ? (
                            <Tag color="purple" title="Delivered against a shop drawing" style={{ margin: 0 }}>
                              Shop drawing
                            </Tag>
                          ) : (
                            <Tag style={{ margin: 0 }}>Sheet</Tag>
                          )}
                        </td>
                        {/* The discipline with the project's partners for it beneath (owner, 2026-10-16). */}
                        <td><DisciplineWithPartners projectId={item?.projectId?._id || item?.projectId} discipline={item?.discipline} /></td>
                        <td>{item?.projectId?.title}</td>
                        <td>
                          {moment(item?.revisionDate).format("DD/MM/YYYY")}
                        </td>
                        <td>{item?.revisionNumber}</td>
                        <td>{item?.revitFileName}</td>
                        <td>{item?.sequenceNumber}</td>
                        <td>{item?.sheetName}<VersionChip docId={item?._id} rev={item?.revisionNumber ?? 0} count={item?.versionCount || 0} /></td>
                        <td>{item?.sheetNumber}</td>
                        <td>{item?.sheetStatus}</td>
                        <td>{moment(item?.updatedAt).format("DD/MM/YYYY")}</td>
                        <td className="pdf-svg cursor-pointer">
                          <div className="d-flex align-items-center">
                            {/* Read it, rather than download it and hope.
                                Opens the sheet on the canvas with
                                everything it is connected to beside it. */}
                            <button
                              className="border-none mr-2"
                              onClick={() => setCanvasSheet(item)}
                              title="Open on canvas with related sheets"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="17"
                                height="17"
                                fill="currentColor"
                                viewBox="0 0 16 16"
                              >
                                <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8M1.173 8a13 13 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5s3.879 1.168 5.168 2.457A13 13 0 0 1 14.828 8q-.086.13-.195.288c-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5s-3.879-1.168-5.168-2.457A13 13 0 0 1 1.172 8z" />
                                <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5M4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0" />
                              </svg>
                            </button>
                            <button
                              className="border-none mr-2"
                              onClick={() => {
                                extractPdfFromZip(item?.link);
                                eventTrack(
                                  "Download-Doc-Control-PDF-Action",
                                  "Download-Doc-Control-PDF",
                                  userInfoRedux?.name,
                                  "+1",
                                  false,
                                  "+1"
                                );
                              }}
                              title={canPreview(item?.link) && /\.pdf/i.test(item?.link || "") ? "Download PDF" : `Download ${(extOf(item?.originalFileName || item?.link) || "file").toUpperCase()}`}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                fill="currentColor"
                                className="bi bi-file-earmark-pdf-fill"
                                viewBox="0 0 16 16"
                              >
                                <path d="M5.523 12.424q.21-.124.459-.238a8 8 0 0 1-.45.606c-.28.337-.498.516-.635.572l-.035.012a.3.3 0 0 1-.026-.044c-.056-.11-.054-.216.04-.36.106-.165.319-.354.647-.548m2.455-1.647q-.178.037-.356.078a21 21 0 0 0 .5-1.05 12 12 0 0 0 .51.858q-.326.048-.654.114m2.525.939a4 4 0 0 1-.435-.41q.344.007.612.054c.317.057.466.147.518.209a.1.1 0 0 1 .026.064.44.44 0 0 1-.06.2.3.3 0 0 1-.094.124.1.1 0 0 1-.069.015c-.09-.003-.258-.066-.498-.256M8.278 6.97c-.04.244-.108.524-.2.829a5 5 0 0 1-.089-.346c-.076-.353-.087-.63-.046-.822.038-.177.11-.248.196-.283a.5.5 0 0 1 .145-.04c.013.03.028.092.032.198q.008.183-.038.465z" />
                                <path
                                  fillRule="evenodd"
                                  d="M4 0h5.293A1 1 0 0 1 10 .293L13.707 4a1 1 0 0 1 .293.707V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2m5.5 1.5v2a1 1 0 0 0 1 1h2zM4.165 13.668c.09.18.23.343.438.419.207.075.412.04.58-.03.318-.13.635-.436.926-.786.333-.401.683-.927 1.021-1.51a11.7 11.7 0 0 1 1.997-.406c.3.383.61.713.91.95.28.22.603.403.934.417a.86.86 0 0 0 .51-.138c.155-.101.27-.247.354-.416.09-.181.145-.37.138-.563a.84.84 0 0 0-.2-.518c-.226-.27-.596-.4-.96-.465a5.8 5.8 0 0 0-1.335-.05 11 11 0 0 1-.98-1.686c.25-.66.437-1.284.52-1.794.036-.218.055-.426.048-.614a1.24 1.24 0 0 0-.127-.538.7.7 0 0 0-.477-.365c-.202-.043-.41 0-.601.077-.377.15-.576.47-.651.823-.073.34-.04.736.046 1.136.088.406.238.848.43 1.295a20 20 0 0 1-1.062 2.227 7.7 7.7 0 0 0-1.482.645c-.37.22-.699.48-.897.787-.21.326-.275.714-.08 1.103"
                                />
                              </svg>
                            </button>
                            <button
                              className="border-none"
                              onClick={() => openIssueTool(item)}
                              title="Issue Document"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                fill="currentColor"
                                viewBox="0 0 16 16"
                              >
                                <path d="M12.854.146a.5.5 0 0 0-.707 0L10.5 1.793 14.207 5.5l1.647-1.646a.5.5 0 0 0 0-.708l-3-3zm.646 6.061L9.793 2.5 3.293 9H3.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.207l6.5-6.5zm-7.468 7.468A.5.5 0 0 1 6 13.5V13h-.5a.5.5 0 0 1-.5-.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.5-.5V10h-.5a.499.499 0 0 1-.175-.032l-.179.178a.5.5 0 0 0-.11.168l-2 5a.5.5 0 0 0 .65.65l5-2a.5.5 0 0 0 .168-.11l.178-.178z"/>
                              </svg>
                            </button>
                            {canShareSheets && (
                              <button
                                className="border-none ml-2"
                                onClick={() => setShareSheet(item)}
                                title="Share this sheet (read-only)"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  fill="currentColor"
                                  viewBox="0 0 16 16"
                                >
                                  <path d="M13.5 1a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3M11 2.5a2.5 2.5 0 1 1 .603 1.628l-6.718 3.12a2.5 2.5 0 0 1 0 1.504l6.718 3.12a2.5 2.5 0 1 1-.488.876l-6.718-3.12a2.5 2.5 0 1 1 0-3.256l6.718-3.12A2.5 2.5 0 0 1 11 2.5m-8.5 4a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3m11 5.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            ) : (
              <tbody>
                <tr>
                  <td>No record Found</td>
                </tr>
              </tbody>
            )}
          </table>
        </div>

        <div className="asset-pagination-div mx-auto">
          <Pagination
            totalRecords={totalRecords}
            totalPage={totalPage}
            page={page + 1}
            onPageChange={(pageNo) => setPage(pageNo - 1)}
            onLimitChange={(count) => {
              data.current = { ...data.current, limit: count };
              if (page === 0) {
                getDocAPI(data);
              } else {
                setPage(0);
              }
            }}
            limit={data.current?.limit}
          />
        </div>
      </div>

      {coversheet && (
        <Preview
          openPreview={openPreview}
          setOpenPreview={setOpenPreview}
          data={coversheet}
          disciplines={disciplines}
        />
      )}

      {/* Issue Tool Modal */}
      <IssueTool
        isOpen={issueOpen}
        onClose={() => setIssueOpen(false)}
        documentUrl={selectedDocForIssue?.url}
        documentName={selectedDocForIssue?.name}
        sheetRef={selectedDocForIssue?._id ? { projectId: selectedDocForIssue.projectId || projectId, docId: selectedDocForIssue._id } : null}
        userInfo={{
          name: userInfoRedux?.name,
          role: userInfoRedux?.admin ? 'Admin' : userInfoRedux?.companyAdmin ? 'Company Admin' : 'User'
        }}
        onSaveIssue={handleSaveIssue}
      />

      <UploadDocumentModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        projects={projects}
        projectId={projectId}
        disciplines={disciplines}
        onUploaded={() => { setUploadOpen(false); refresh(); }}
      />

      <ShareManySheetsModal
        sheets={selectedRows}
        projectId={selectedRows[0]?.projectId?._id || selectedRows[0]?.projectId}
        open={shareManyOpen}
        onClose={() => setShareManyOpen(false)}
        onDone={() => setSelectedIds([])}
      />

      <ShareResourceModal
        type="sheet"
        id={shareSheet?._id}
        projectId={shareSheet?.projectId?._id || shareSheet?.projectId}
        title={shareSheet ? `${shareSheet.sheetNumber ? `${shareSheet.sheetNumber} - ` : ""}${shareSheet.sheetName}` : ""}
        open={!!shareSheet}
        onClose={() => setShareSheet(null)}
      />

      <SheetCanvasDrawer
        doc={canvasSheet}
        open={!!canvasSheet}
        onClose={() => setCanvasSheet(null)}
      />

      {/* What this sheet said, each time it was issued. */}
      <Modal
        open={!!versionsFor}
        onCancel={() => setVersionsFor(null)}
        footer={[<Button key="c" onClick={() => setVersionsFor(null)}>Close</Button>]}
        width={720}
        destroyOnClose
        // The sheet name filed by a set import already begins with the
        // sheet number, so prefixing it again gave "3-E1.2 - 3-E1.2 - ...".
        title={versionsFor
          ? (() => {
            const name = versionsFor.doc?.sheetName || "Versions";
            const num = versionsFor.doc?.sheetNumber;
            return num && !name.startsWith(num) ? `${num} - ${name}` : name;
          })()
          : "Versions"}
      >
        <Table
          size="small"
          loading={versionsBusy}
          rowKey={(r, i) => `${r.revisionNumber}-${i}`}
          dataSource={versionsFor?.versions || []}
          pagination={false}
          columns={[
            {
              title: "V",
              dataIndex: "revisionNumber",
              width: 70,
              render: (v, r) => (r.current
                ? <Tag color="green">v{v} current</Tag>
                : <Tag>v{v}</Tag>),
            },
            {
              title: "Issued",
              dataIndex: "createdAt",
              width: 150,
              render: (v) => (v ? moment(v).format("DD/MM/YYYY HH:mm") : "—"),
            },
            {
              title: "Title as read",
              dataIndex: "title",
              render: (v) => v || <span style={{ color: "#9ca3af" }}>—</span>,
            },
            {
              title: "Delivered in",
              dataIndex: "sourceFileName",
              render: (v, r) => (
                <span title={v || ""}>
                  {v || "—"}{r.page ? ` (p${r.page})` : ""}
                </span>
              ),
            },
            {
              title: "",
              width: 90,
              render: (_, r) => (r.link
                ? <Button size="small" onClick={() => extractPdfFromZip(r.link)}>Download</Button>
                : null),
            },
          ]}
        />
      </Modal>
    </>
  );
}